import { Option } from "@/components/forms/inputs/Autocomplete/types";
import Vue from "vue";

export enum FormComponentType {
    Repeater = "repeater",
    Number = "number",
    Checkbox = "checkbox",
    Date = "date",
    Autocomplete = "autocomplete",
}

/**
 * Konverterar inskickat värde beroende på typ som skickats in. Kan t.ex användas för att konvertera "1" till 1
 */
const convertByValueType = ({
    value,
    valueType,
}: {
    value?: unknown;
    valueType?: "number" | "string" | "boolean" | "complex";
}) => {
    if (valueType === "number") {
        return Number(value);
    }

    if (valueType === "string") {
        return String(value);
    }

    if (valueType === "boolean") {
        if (typeof value === "string") {
            if (value.toLowerCase() === "true") {
                return true;
            }

            if (value.toLowerCase() === "false") {
                return false;
            }

            return Boolean(value);
        }

        return Boolean(value);
    }

    return value;
};

/**
 * Ser till att nummer ser ut som BE vill ha det. Ändrar , till . och konverterar till number
 */
const getConvertedNumber = ({ numberValue }: { numberValue: number | string }) => {
    if (typeof numberValue === "string") {
        /**
         * Konvertera till null om det är tomt sträng. Tom sträng betyder inget värde och backend vill ha
         * null för att markera att ett fält ska tömmas
         */
        if (!numberValue) {
            return null;
        }

        return Number(numberValue.replace(",", "."));
    }

    return numberValue;
};

/**
 * Kollar och returnerar om värdet är tomt. Tomt objekt/sträng/array räknas som tomt
 */
const isEmptyValue = ({ value }: { value?: unknown }) => {
    if (value === null) {
        return true;
    }

    // Nummer 0 ska inte räknas som tomt
    if (typeof value === "number") {
        return false;
    }

    if (typeof value === "string") {
        return !value;
    }

    if (Array.isArray(value)) {
        return !value.length;
    }

    if (typeof value === "object" && value && Object.keys(value).length === 0) {
        return true;
    }

    return false;
};

type KFieldComponent = {
    kType: FormComponentType;
    /**
     * Funktion i komponenten som anropas för att manipulera värdet fältet har
     */
    convertData?: (props: { value: unknown | unknown[] }) => unknown | unknown[];
    kValueType?: () => "boolean" | "number" | "string" | "complex";
    /**
     * Ska tomma värden på fältet tas bort vid submit? Kan användas för att filtrera bort tomma värden vid
     * submit
     */
    ignoreEmptyData?: () => boolean;
    /**
     * Ska värdet på fältet tas bort vid submit? Kan användas för att ha med fält i formuläret som
     * ska användas för triggers, men som innehåller data som inte är intressant att submitta
     */
    ignoreValue?: () => boolean;
    $options: {
        propsData: {
            label?: string;
            placeholder?: string;
        };
    };
};

/**
 * Funktion som tar emot formulärdata och registry from Formulate (och i framtiden Formkit).
 *
 * registry används för att avgöra vilken typ av input det är (t.ex nummerfält).
 * Baserat på det kan datan konverteras till ett format som backend vill ha.
 * T.ex är värdet i nummerfält eller datumfält inte samma som det som backend förväntar sig. Vi kan
 * använda convertFormData för att konvertera all data till rätt format innan det skickas till BE.
 *
 * converters är funktioner som används för att vidare formattera datan för specifika fält innan det skickas till BE.
 * T.ex en repeater som har en array med objekt som value men backend vill ha en array med nummer
 */
export const convertFormData = <F>({
    formData,
    registry,
    converters,
    displayMode,
}: {
    formData: Object & F;
    registry: Map<
        string,
        Vue & {
            $parent: KFieldComponent;
        }
    >;
    converters?: Array<{
        fieldName: string;
        converter: ({ value }: { value: unknown | unknown[] }) => unknown | unknown[];
    }>;
    /**
     * displayMode true gör att fieldLabels populeras i formulärdatan innehållandes objekt med key fältets name
     * och value fältets label eller placeholder.
     *
     * Om fältet är KAutocomplete kommer value som returneras vara ett objekt med value och displayLabel med label
     * taget från vald option
     *
     * Kan användas för att kunna skriva ut humanreadable labels för
     * fält (t.ex i KFilter som skriver ut *fältets namn*: *värde*).
     */
    displayMode?: boolean;
}): F | (F & { fieldLabels: { [key: string]: string } }) => {
    const fieldLabels: { [key: string]: string } = {};
    const convertedFormData = Object.fromEntries(
        Object.entries(formData)
            .reduce((entries: any, [key, value]) => {
                // Hämtar registrerade komponenten från Formulate
                const registeredComponent = registry.get(key);
                // Hämtar konverteringsfunktionen för fältet, om det finns en satt
                const converter =
                    registeredComponent?.$parent?.convertData ??
                    converters?.find?.(({ fieldName }) => fieldName === key)?.converter;
                const ignoreEmptyData = registeredComponent?.$parent?.ignoreEmptyData?.();
                const ignoreValue = registeredComponent?.$parent?.ignoreValue?.();

                if (ignoreValue) {
                    return [...entries];
                }

                if (!registeredComponent) {
                    return [...entries, [key, converter ? converter({ value }) : value]];
                }

                if (displayMode) {
                    const fieldLabel =
                        registeredComponent.$parent.$options.propsData.label ??
                        registeredComponent.$parent.$options.propsData.placeholder;

                    if (fieldLabel) {
                        fieldLabels[key] = fieldLabel;
                    }
                }

                /**
                 * Default case skickar tillbaka datan oförändrad
                 */
                switch (registeredComponent.$parent.kType) {
                    /**
                     * Repeater
                     */
                    case FormComponentType.Repeater: {
                        const filteredValue = value
                            ? (value as unknown[]).filter(
                                  (singleValue) =>
                                      // Filtrerar bort alla tomma objekt
                                      typeof value === "object" &&
                                      Object.keys(singleValue as Object).length > 0 &&
                                      // Filtrerar bort alla objekt som bara innehåller värden som är null/undefined
                                      Object.values(singleValue as Object).some((value) => {
                                          return !!(typeof value === "number" || value);
                                      })
                              )
                            : [];

                        return [...entries, [key, converter ? converter({ value: filteredValue }) : filteredValue]];
                    }
                    case FormComponentType.Number: {
                        const convertedValue = getConvertedNumber({ numberValue: value as number | string });

                        if (ignoreEmptyData && !convertedValue) {
                            return [...entries];
                        }

                        return [...entries, [key, convertedValue]];
                    }
                    case FormComponentType.Checkbox: {
                        const valueType = registeredComponent.$parent.kValueType?.();

                        // toggles ska inte följa med i formulärdatan vid submit. Används bara internt i KCheckbox
                        if (key.endsWith("-k-checkbox-toggle")) {
                            return [...entries];
                        }

                        /*
                        Om convertData är exposat i komponenten eller om converter är satt i KForm som ska påverka
                        fältet
                     */
                        if (converter) {
                            // Konvertera värdet med convertern
                            const convertedValue = converter({ value });

                            /*
                            Om ignoreEmptyData är satt i komponenten och värdet är tomt kommer formdata inte få
                            appendat innehåll
                         */
                            if (ignoreEmptyData && isEmptyValue({ value: convertedValue })) {
                                return [...entries];
                            }

                            return [...entries, [key, convertedValue]];
                        }

                        /*
                        Om ignoreEmptyData är satt i komponenten och värdet är tomt kommer formdata inte få
                        appendat innehåll
                     */
                        if (ignoreEmptyData && isEmptyValue({ value })) {
                            return [...entries];
                        }

                        /*
                        Om värdet är en array. KCheckbox med flera options kommer returnera data som array. En enskild
                        checkbox gör inte det
                     */
                        if (Array.isArray(value)) {
                            // Rensa bort tomma värden i arrayen, om ignoreEmptyData är satt i komponenten
                            const values = ignoreEmptyData
                                ? value.filter((singleValue) => !isEmptyValue({ value: singleValue }))
                                : value;

                            // Konvertera varje värde i arrayen och appenda i formdata
                            return [
                                ...entries,
                                [
                                    key,
                                    values.map((singleValue) => convertByValueType({ valueType, value: singleValue })),
                                ],
                            ];
                        }

                        // Appenda i formdata om inget ovan stämmer
                        return [...entries, [key, convertByValueType({ valueType, value })]];
                    }
                    case FormComponentType.Date: {
                        const convertedValue = converter ? converter({ value }) : value;

                        if (ignoreEmptyData && isEmptyValue({ value: convertedValue })) {
                            return [...entries];
                        }

                        return [...entries, [key, convertedValue]];
                    }
                    case FormComponentType.Autocomplete: {
                        let convertedData = converter ? converter({ value }) : value;

                        /*
                        Om ignoreEmptyData är satt i komponenten och värdet är tomt kommer formdata inte få
                        appendat innehåll
                     */
                        if (ignoreEmptyData && isEmptyValue({ value: convertedData })) {
                            return [...entries];
                        }

                        if (displayMode && convertedData) {
                            const selectedOptions = (
                                registeredComponent.$parent as unknown as KFieldComponent & {
                                    getSelectedOption: () => Option[];
                                }
                            ).getSelectedOption();

                            if (Array.isArray(convertedData)) {
                                convertedData = convertedData.map((value, index) => {
                                    return {
                                        value,
                                        displayLabel: selectedOptions?.[index]?.label,
                                    };
                                });
                            } else {
                                convertedData = {
                                    value: convertedData,
                                    displayLabel: selectedOptions?.[0]?.label,
                                };
                            }
                        }

                        return [...entries, [key, convertedData]];
                    }
                    default: {
                        /*
                        Om ignoreEmptyData är satt i komponenten och värdet är tomt kommer formdata inte få
                        appendat innehåll
                     */
                        if (ignoreEmptyData && isEmptyValue({ value: converter ? converter({ value }) : value })) {
                            return [...entries];
                        }

                        return [...entries, [key, converter ? converter({ value }) : value]];
                    }
                }
            }, [] as [string, unknown][])
            .sort((a: any, b: any) => (a[0] > b[0] ? 1 : -1))
    ) as F;

    if (Object.keys(fieldLabels).length) {
        return {
            fieldLabels,
            ...convertedFormData,
        };
    }

    return convertedFormData;
};
